import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send, WarningAmber, CheckCircle } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentRun } from '../../hooks/useAgentRun';

function formatMessageTime(createdAt?: string) {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function MessageItem({
  message,
  onResolveHitl,
  onDecisionSelected,
}: {
  message: any
  onResolveHitl?: (decision: any) => void
  onDecisionSelected?: (messageId: string, decision: any, selectedLabel: string) => void
}) {
  if (message.role === 'user') {
    return (
      <Paper
        sx={{
          p: 1.5,
          alignSelf: 'flex-end',
          width: 'fit-content',
          minWidth: 120,
          maxWidth: '78%',
          bgcolor: '#000000',
          borderRadius: '18px 18px 4px 18px',
          boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
        }}
      >
        <Typography variant="body2" sx={{ color: '#f8f9f9', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {message.text}
        </Typography>
      </Paper>
    );
  }

  if (message.role === 'assistant') {
    const answer = message.answer || {};
    const timeLabel = formatMessageTime(message.createdAt);
    return (
      <Paper
        sx={{
          p: 0,
          alignSelf: 'flex-start',
          width: '100%',
          minWidth: 160,
          maxWidth: '88%',
          bgcolor: '#fcfdff',
          border: '1px solid #d8dee9',
          borderLeft: '4px solid #4b5563',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 0.85,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e5e7eb',
            bgcolor: '#f8fafc',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.35, color: '#334155' }}>
            AGENTE
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            {timeLabel}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.6,
            pt: 1.25,
            pb: 1.1,
            color: 'text.primary',
            fontSize: '0.875rem',
            lineHeight: 1.65,
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            '& p': { m: 0, mb: 1 },
            '& p:last-of-type': { mb: 0 },
            '& ul, & ol': { m: 0, mb: 1, pl: 2.2 },
            '& li': { mb: 0.35 },
            '& li:last-child': { mb: 0 },
            '& strong': { fontWeight: 700 },
            '& h1, & h2, & h3, & h4': {
              mt: 0,
              mb: 0.6,
              fontWeight: 700,
              color: '#0f172a',
            },
            '& code': {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              bgcolor: 'rgba(15, 23, 42, 0.05)',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              px: 0.35,
              py: 0.05,
              borderRadius: 0.6,
            },
            '& pre': {
              m: 0,
              mb: 1,
              p: 1,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              bgcolor: 'rgba(15, 23, 42, 0.05)',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              borderRadius: 1,
            },
            '& pre code': {
              px: 0,
              py: 0,
              border: 'none',
              bgcolor: 'transparent',
            },
            '& table': {
              width: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'collapse',
            },
            '& th, & td': {
              border: '1px solid #dbe3ec',
              p: 0.5,
              textAlign: 'left',
              verticalAlign: 'top',
              overflowWrap: 'anywhere',
            },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {answer.answer_text || 'No answer text provided.'}
          </ReactMarkdown>
        </Box>
        <Stack
          direction="row"
          spacing={0.8}
          sx={{
            px: 1.25,
            pb: 1.2,
            pt: 0.35,
            flexWrap: 'wrap',
            gap: 0.45,
          }}
        >
          <Chip size="small" label={`Confianza: ${answer?.confidence?.level || 'desconocida'}`} sx={{ bgcolor: '#eef2f7', color: '#334155', border: '1px solid #dbe3ec', fontWeight: 500 }} />
          <Chip size="small" label={`Puntuación: ${Math.round((answer?.confidence?.score || 0) * 100)}%`} sx={{ bgcolor: '#eef2f7', color: '#334155', border: '1px solid #dbe3ec', fontWeight: 500 }} />
          <Chip size="small" label={`Fuentes: ${(answer?.evidence || []).length}`} sx={{ bgcolor: '#eef2f7', color: '#334155', border: '1px solid #dbe3ec', fontWeight: 500 }} />
          <Chip size="small" label={`Herramientas: ${(answer?.tools_used || []).length}`} sx={{ bgcolor: '#eef2f7', color: '#334155', border: '1px solid #dbe3ec', fontWeight: 500 }} />
        </Stack>
      </Paper>
    );
  }

  if (message.role === 'hitl') {
    const buildDecisionFromOption = (optionKey: string) => {
      const kind = message.request?.kind;
      const isLiteralAction = optionKey === 'approve' || optionKey === 'cancel';
      if (isLiteralAction) {
        return { action: optionKey, selected_option_key: optionKey };
      }
      if (kind === 'project_disambiguation') {
        return { action: 'select_project', selected_option_key: optionKey };
      }
      if (kind === 'source_disambiguation') {
        return { action: 'select_source', selected_option_key: optionKey };
      }
      return { action: 'select_documents', selected_option_key: optionKey };
    };

    let selectedLabel = '';
    if (message.decision?.selected_option_key && message.request?.options) {
      const selectedOption = message.request.options.find(
        (opt: any) => opt.key === message.decision.selected_option_key,
      );
      selectedLabel = selectedOption?.label || message.decision.selected_option_key;
    }
    if (message.decision?.free_text) {
      selectedLabel = `"${message.decision.free_text}"`;
    }

    const hasOptions = message.request?.options && message.request.options.length > 0;
    const allowsFreeText = message.request?.allow_free_text;

    return (
      <Paper
        sx={{
          p: 1.5,
          alignSelf: 'flex-start',
          width: 'fit-content',
          minWidth: 180,
          maxWidth: '78%',
          bgcolor: '#fef3c7',
          border: '1px solid #fbbf24',
          borderRadius: '18px 18px 18px 4px',
          boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <WarningAmber sx={{ color: '#b45309', mt: 0.5, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309' }}>
              Decision required
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
              {message?.request?.question || 'Resolve ambiguity'}
            </Typography>
            {message?.request?.context && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#92400e' }}>
                {message.request.context}
              </Typography>
            )}

            {!selectedLabel && hasOptions && (
              <>
                <Divider sx={{ my: 1, borderColor: '#fbbf24' }} />
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {message.request.options.map((option: any) => (
                    <Button
                      key={option.key}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#b45309',
                        color: '#b45309',
                        '&:hover': {
                          bgcolor: 'rgba(180, 83, 9, 0.08)',
                          borderColor: '#b45309',
                        },
                      }}
                      onClick={() => {
                        const decision = buildDecisionFromOption(option.key);
                        onDecisionSelected?.(message.id, decision, option.label);
                        onResolveHitl?.(decision);
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Stack>
              </>
            )}

            {!selectedLabel && !hasOptions && allowsFreeText && (
              <>
                <Divider sx={{ my: 1, borderColor: '#fbbf24' }} />
                <Typography variant="caption" sx={{ display: 'block', color: '#92400e', fontStyle: 'italic' }}>
                  Write your answer in the chat below
                </Typography>
              </>
            )}

            {selectedLabel && (
              <>
                <Divider sx={{ my: 1, borderColor: '#fbbf24' }} />
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CheckCircle sx={{ color: '#059669', fontSize: '1rem', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
                    Response: {selectedLabel}
                  </Typography>
                </Stack>
              </>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (message.role === 'error') {
    return <Alert severity="error">{message.message || 'Agent error'}</Alert>;
  }

  return null;
}

function ActivityPanel({ items }: { items: Array<{ id: string; text: string; createdAt?: string }> }) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const lastItem = items[items.length - 1];

  useEffect(() => {
    if (!panelRef.current || !shouldStickToBottomRef.current) return;
    panelRef.current.scrollTop = panelRef.current.scrollHeight;
  }, [items.length, lastItem?.id, lastItem?.text, lastItem?.createdAt]);

  useEffect(() => {
    if (!panelRef.current || typeof ResizeObserver === 'undefined') return undefined;

    const element = panelRef.current;
    const observer = new ResizeObserver(() => {
      if (!shouldStickToBottomRef.current) return;
      element.scrollTop = element.scrollHeight;
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const handleScroll = () => {
    if (!panelRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = panelRef.current;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
    shouldStickToBottomRef.current = distanceFromBottom <= 32;
  };

  if (!items.length) return null;

  return (
    <Paper
      ref={panelRef}
      onScroll={handleScroll}
      sx={{
        p: 1.25,
        mb: 1.2,
        bgcolor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '18px',
        minHeight: 120,
        maxHeight: 260,
        overflowY: 'auto',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: '#334155', fontWeight: 800, letterSpacing: 0.3 }}>
          Agent Activity
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748b' }}>
          Live
        </Typography>
      </Box>
      <Stack spacing={0.75}>
        {items.map((item) => {
          const timeLabel = formatMessageTime(item.createdAt);
          return (
            <Box
              key={item.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                gap: 1,
                alignItems: 'flex-start',
                p: 0.9,
                borderRadius: '14px',
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.15 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8', mb: 0.5 }} />
                <Box sx={{ width: 1, minHeight: 18, bgcolor: '#e2e8f0' }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.2 }}>
                  {timeLabel}
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {item.text}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export const ChatPanel = React.memo(function ChatPanel({ hideHeader = false }: { hideHeader?: boolean }) {
  const { state, sendMessage, resolveHitl } = useAgentRun();
  const [draft, setDraft] = useState('');
  const [localDecisions, setLocalDecisions] = useState<Record<string, any>>({});
  const inputRef = useRef<any>(null);
  const messagesViewportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!messagesViewportRef.current) return;
    messagesViewportRef.current.scrollTop = messagesViewportRef.current.scrollHeight;
  }, [state.messages.length, state.activityLog.length, state.isBusy]);

  const canSend = useMemo(
    () => !!draft.trim() && !state.isBusy && state.connectionStatus === 'open',
    [draft, state],
  );

  const handleDecisionSelected = (messageId: string, decision: any, selectedLabel: string) => {
    setLocalDecisions((prev) => ({
      ...prev,
      [messageId]: decision,
    }));
    console.log('✅ Decision selected locally:', { messageId, decision, selectedLabel });
  };

  const onSubmit = () => {
    if (!canSend) return;

    const pendingHitl = state.pendingHitl;
    const hasOptions = pendingHitl?.request?.options && pendingHitl.request.options.length > 0;
    const allowsFreeText = pendingHitl?.request?.allow_free_text;

    if (pendingHitl && !hasOptions && allowsFreeText) {
      const decision = { action: 'add_context', free_text: draft.trim() };
      const hitlMessageId = state.messages.find((m: any) => m.role === 'hitl' && !m.decision)?.id;
      if (hitlMessageId) {
        setLocalDecisions((prev) => ({
          ...prev,
          [hitlMessageId]: decision,
        }));
      }

      resolveHitl(decision);
      setDraft('');
    } else {
      sendMessage(draft.trim());
      setDraft('');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {!hideHeader && (
        <>
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1 }} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Agent</Typography>
            <Chip size="small" label={state.isBusy ? 'Working' : 'Connected'} color={state.isBusy ? 'info' : 'default'} />
          </Stack>
          <Divider />
        </>
      )}

      <Box ref={messagesViewportRef} sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.2, bgcolor: '#efeae2' }}>
        {state.messages.map((message: any) => (
          <MessageItem
            key={message.id}
            message={{ ...message, decision: localDecisions[message.id] || message.decision }}
            onResolveHitl={resolveHitl}
            onDecisionSelected={handleDecisionSelected}
          />
        ))}
        {state.isBusy && state.activityLog.length > 0 && (
          <ActivityPanel items={state.activityLog} />
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 1.25, bgcolor: '#f0f2f5' }}>
        {state.connectionStatus !== 'open' && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {state.connectionStatus === 'connecting' ? 'Connecting to agent...' : 'Disconnected from agent. Please refresh the page.'}
          </Alert>
        )}
        <Stack direction="row" spacing={1}>
          <TextField
            ref={inputRef}
            fullWidth
            size="small"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            onMouseDownCapture={(e) => {
              e.stopPropagation();
            }}
            onClickCapture={(e) => {
              e.stopPropagation();
            }}
            onKeyDownCapture={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                onSubmit();
              } else {
                event.stopPropagation();
              }
            }}
            onKeyUpCapture={(event) => {
              event.stopPropagation();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
              } else {
                event.stopPropagation();
              }
            }}
            onKeyUp={(event) => {
              event.stopPropagation();
            }}

            placeholder="Ask about requirements, objects, documents or graph relations..."
            disabled={state.connectionStatus !== 'open'}
            autoComplete="off"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                bgcolor: '#ffffff',
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<Send />}
            disabled={!canSend}
            onClick={onSubmit}
            sx={{
              borderRadius: '999px',
              px: 2.2,
              bgcolor: '#d1d5db',
              color: '#111827',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#9ca3af',
                boxShadow: 'none',
              },
            }}
          >
            Send
          </Button>
        </Stack>
      </Box>
    </Box>
  );
});
